import { useQuery } from "@tanstack/react-query";
import { NAV_FALLBACK_ITEMS, type NavItem } from "@dramapie/shared";
import { Fragment, type MouseEvent } from "react";

import { getNav } from "../lib/api";

/**
 * 本 bundle 就是「工作室」站：线上按 host 认领当前站，本地预览（localhost）
 * 等 host 对不上的场景按 id 兜底，保证「当前站」永远有一个且只有一个。
 */
const DEFAULT_CURRENT_ID = "studio";

export interface NavLinksProps {
  /** 直接给菜单（测试 / 宿主注入），缺省请求 `GET /api/nav`。 */
  items?: readonly NavItem[];
  /** 当前站点 host（测试注入）；缺省 `window.location.host`。 */
  currentHost?: string;
  /** host 对不上时的当前站 id 兜底；缺省 `studio`。 */
  currentId?: string;
  /** `rail`（左轨 12px）| `topbar`（项目列表顶栏 13px，选中带下划线）。 */
  variant?: "rail" | "topbar";
}

function hostOfWindow(): string {
  return typeof window === "undefined" ? "" : window.location.host;
}

/**
 * 跨站导航（P1-W3/W4，2026-11 改版）：工作室 · 画布 · 账户。
 *
 * 用户反馈第 1/3 条把「下拉」改成**平铺一行**：
 *   · 左轨底部（账户信息正上方）：12px、`--ink-3`、hover `--ink`；
 *   · 项目列表顶栏：13px，当前站琥珀 + 2px 下划线。
 *
 * 链接是**整页跳转**（不同站点，不是 SPA 路由），所以用 `<a href>`。
 * 当前站保留 `<a href>` 语义 + `aria-current="page"`，但 `preventDefault`
 * 且退出 tab 序（`aria-disabled`），既不可点，也不会把用户送回当前页。
 * 三条数据仍来自 `GET /api/nav`，失败回退 `NAV_FALLBACK_ITEMS`。
 */
export function NavLinks({
  items,
  currentHost,
  currentId = DEFAULT_CURRENT_ID,
  variant = "rail",
}: NavLinksProps) {
  const host = currentHost ?? hostOfWindow();

  // 后端保证有兜底；placeholderData 让首帧就有三条，断网也不会空着。
  const navQuery = useQuery({
    queryKey: ["nav"],
    queryFn: async (): Promise<readonly NavItem[]> => (await getNav()).items,
    enabled: items === undefined,
    placeholderData: NAV_FALLBACK_ITEMS,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const list: readonly NavItem[] = items ?? navQuery.data ?? NAV_FALLBACK_ITEMS;

  const activeId =
    (host ? list.find((item) => item.host === host)?.id : undefined) ??
    (list.some((item) => item.id === currentId) ? currentId : undefined);

  return (
    <nav className={`dp-nav-links dp-nav-links-${variant}`} aria-label="跨站导航">
      {list.map((item, index) => {
        const current = item.id === activeId;
        return (
          <Fragment key={item.id}>
            {index > 0 ? (
              <span className="dp-nav-links-sep" aria-hidden="true">
                ·
              </span>
            ) : null}
            <a
              href={item.url}
              className={`dp-nav-link${current ? " is-current" : ""}`}
              {...(current
                ? {
                    "aria-current": "page" as const,
                    "aria-disabled": true,
                    tabIndex: -1,
                    title: `${item.name}（当前站点）`,
                    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                      event.preventDefault();
                    },
                  }
                : {})}
            >
              {item.name}
            </a>
          </Fragment>
        );
      })}
    </nav>
  );
}
